import React from 'react'
import { FileUpload } from 'submission/components/deal-submission/file-upload'
import useFileUploader from 'hooks/useFileUploader'
import type {
  CuratorProof,
  EquityFormData,
} from 'submission/lib/equity-form-defaults'

type Props = {
  form: EquityFormData
  setForm: React.Dispatch<React.SetStateAction<EquityFormData>>
  errors: Record<string, string>
}

const FundsSection: React.FC<Props> = ({ form, setForm, errors }) => {
  const { uploadFile } = useFileUploader()

  const updateProof = (idx: number, k: keyof CuratorProof, v: string) =>
    setForm((p) => ({
      ...p,
      curatorProofs: p.curatorProofs.map((row, i) =>
        i === idx ? { ...row, [k]: v } : row,
      ),
    }))

  const uploadProof = (idx: number, file: File | null) => {
    if (!file) {
      updateProof(idx, 'value', '')
      return
    }
    uploadFile(
      { file, path: `submission/curator-proof-${Date.now()}-${file.name}` },
      (res) =>
        setForm((p) => ({
          ...p,
          curatorProofs: p.curatorProofs.map((row, i) =>
            i === idx
              ? { ...row, value: res?.url ?? res?.key ?? res ?? '' }
              : row,
          ),
        })),
    )
  }

  return (
    <section className="bg-white border border-slate-200 rounded p-6">
      <h2 className="text-lg font-semibold text-slate-900 mb-4">
        Funds &amp; royalties
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="flex flex-col text-sm md:col-span-2">
          <span className="text-slate-700 mb-1">
            Project receiving wallet *
          </span>
          <input
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={form.projectReceivingWallet}
            onChange={(e) =>
              setForm((p) => ({
                ...p,
                projectReceivingWallet: e.target.value,
              }))
            }
          />
          {errors.projectReceivingWallet && (
            <span className="text-red-600 text-xs mt-1">
              {errors.projectReceivingWallet}
            </span>
          )}
        </label>
        <label className="flex flex-col text-sm">
          <span className="text-slate-700 mb-1">
            Curator commitment (USD) *
          </span>
          <input
            type="number"
            min={0}
            step="any"
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={form.curatorCommitmentAmount}
            onChange={(e) =>
              setForm((p) => ({
                ...p,
                curatorCommitmentAmount: e.target.value,
              }))
            }
          />
          {errors.curatorCommitmentAmount && (
            <span className="text-red-600 text-xs mt-1">
              {errors.curatorCommitmentAmount}
            </span>
          )}
        </label>
        <label className="flex flex-col text-sm">
          <span className="text-slate-700 mb-1">Curator wallet *</span>
          <input
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={form.curatorWallet}
            onChange={(e) =>
              setForm((p) => ({ ...p, curatorWallet: e.target.value }))
            }
          />
          {errors.curatorWallet && (
            <span className="text-red-600 text-xs mt-1">
              {errors.curatorWallet}
            </span>
          )}
        </label>
        <label className="flex flex-col text-sm">
          <span className="text-slate-700 mb-1">Curator fee (bips) *</span>
          <input
            type="number"
            min={0}
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={form.curatorFeeBips}
            onChange={(e) =>
              setForm((p) => ({ ...p, curatorFeeBips: e.target.value }))
            }
          />
          {errors.curatorFeeBips && (
            <span className="text-red-600 text-xs mt-1">
              {errors.curatorFeeBips}
            </span>
          )}
        </label>
        <label className="flex flex-col text-sm">
          <span className="text-slate-700 mb-1">Project royalty (bips) *</span>
          <input
            type="number"
            min={0}
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={form.projectRoyaltyBips}
            onChange={(e) =>
              setForm((p) => ({
                ...p,
                projectRoyaltyBips: e.target.value,
              }))
            }
          />
          {errors.projectRoyaltyBips && (
            <span className="text-red-600 text-xs mt-1">
              {errors.projectRoyaltyBips}
            </span>
          )}
        </label>
        <label className="flex flex-col text-sm">
          <span className="text-slate-700 mb-1">Curator royalty (bips) *</span>
          <input
            type="number"
            min={0}
            className="border rounded px-2 py-1 text-slate-900 bg-white"
            value={form.curatorRoyaltyBips}
            onChange={(e) =>
              setForm((p) => ({
                ...p,
                curatorRoyaltyBips: e.target.value,
              }))
            }
          />
          {errors.curatorRoyaltyBips && (
            <span className="text-red-600 text-xs mt-1">
              {errors.curatorRoyaltyBips}
            </span>
          )}
        </label>
      </div>

      {errors.royaltyBips && (
        <p className="text-red-600 text-xs mt-2">{errors.royaltyBips}</p>
      )}

      <h3 className="text-sm font-medium text-slate-700 mt-4 mb-2">
        Curator proofs
      </h3>
      <div className="flex flex-col gap-3">
        {form.curatorProofs.map((row, i) => (
          <div
            key={i}
            className="border rounded p-3 flex flex-col md:flex-row md:items-end gap-3"
          >
            <label className="flex flex-col text-sm">
              <span className="text-slate-700 mb-1">Type</span>
              <select
                className="border rounded px-2 py-1 text-slate-900 bg-white"
                value={row.type}
                onChange={(e) =>
                  updateProof(i, 'type', e.target.value as CuratorProof['type'])
                }
              >
                <option value="onchain">onchain</option>
                <option value="document">document</option>
              </select>
            </label>
            {row.type === 'onchain' ? (
              <label className="flex flex-col text-sm flex-1">
                <span className="text-slate-700 mb-1">URL</span>
                <input
                  className="border rounded px-2 py-1 text-slate-900 bg-white"
                  value={row.value}
                  onChange={(e) => updateProof(i, 'value', e.target.value)}
                />
              </label>
            ) : (
              <div className="flex-1">
                <span className="text-sm text-slate-700 mb-1 block">
                  Document
                </span>
                <FileUpload
                  mode="document"
                  accept="application/pdf"
                  buttonLabel={row.value ? 'Replace PDF' : 'Upload PDF'}
                  helperText="PDF, up to 25 MB"
                  maxSizeMB={25}
                  errorStrings={{
                    fileCount: 'Please upload one document.',
                    fileType: 'PDF only',
                    fileSize: 'Max 25 MB',
                    unreadable: 'File appears corrupted',
                    password: 'File appears protected',
                    empty: 'File is empty',
                    generic: 'Upload failed. Please try again.',
                    linkDropNotAllowed: 'Cannot drop a link',
                  }}
                  ariaLabel={`Upload curator proof ${i + 1}`}
                  onFileSelect={(file) => uploadProof(i, file)}
                  currentFile={
                    row.value
                      ? new File([], 'proof.pdf', { type: 'application/pdf' })
                      : null
                  }
                  onRemove={() => updateProof(i, 'value', '')}
                />
              </div>
            )}
            <button
              type="button"
              className="px-3 py-1 border rounded text-sm"
              onClick={() =>
                setForm((p) => ({
                  ...p,
                  curatorProofs: p.curatorProofs.filter((_, idx) => idx !== i),
                }))
              }
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          className="self-start px-3 py-1 border rounded text-sm"
          onClick={() =>
            setForm((p) => ({
              ...p,
              curatorProofs: [
                ...p.curatorProofs,
                { type: 'onchain', value: '' },
              ],
            }))
          }
        >
          Add proof
        </button>
      </div>
    </section>
  )
}

export default FundsSection
